import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { RolePageRecorder } from '@/auth/RolePageRecorder';
import { resetRoleBootForTests, runRoleBootstrap } from '@/auth/roleBoot';
import type { BootstrapOutcome } from '@/auth/roleSelection';
import { useRoleSwitchProgress } from '@/auth/roleSwitchProgress';

/**
 * Paths that never wait for the role check (sign-in pages, the no-access page, and the
 * tenant administration's invitation and Request access pages: a person invited for the
 * first time has no role until they accept).
 */
export const ROLE_GATE_OPEN_PATHS = [
  '/',
  '/sso',
  '/resetpassword',
  '/noaccess',
  '/invitation',
  '/request-access',
];

/** Test helper. */
export const resetRoleGateForTests = (): void => {
  resetRoleBootForTests();
};

/**
 * Page-load role check of a signed-in tab (Authorization-Plan §6.2 step 6,
 * §13.6): before the page renders (and fires its requests), the active role
 * assignment is confirmed with the identity service and becomes this tab's
 * `X-PNET-Expected-Assignment`, and the role's session data is loaded into
 * memory (auth/roleBoot.ts; on a hard refresh the page load already did both
 * before the app rendered). Several roles and none active shows the selection
 * screen first, then continues to the requested (deep) link; no valid role
 * goes to /noaccess. Once the role is confirmed, every navigation is noted as
 * that role's last page (auth/RolePageRecorder.tsx).
 *
 * While the role changes (a switch, a preview start or end: auth/roleSwitchProgress.ts) the
 * page is unmounted and only the overlay shows: the previous role's page would otherwise
 * refetch under the next role's permissions and send itself to /nopermission before the reload.
 */
export const RoleGate = ({
  authenticated,
  pathname,
  fallback,
  children,
}: {
  authenticated: boolean;
  pathname: string;
  fallback: ReactNode;
  children: ReactNode;
}) => {
  const open = !authenticated || ROLE_GATE_OPEN_PATHS.includes(pathname);
  const [outcome, setOutcome] = useState<BootstrapOutcome | null>(null);
  const switching = useRoleSwitchProgress() !== null;

  useEffect(() => {
    if (open) return;
    let active = true;
    void runRoleBootstrap().then((result) => {
      if (active) setOutcome(result);
    });
    return () => {
      active = false;
    };
  }, [open]);

  if (open) return <>{children}</>;
  if (switching) return null;
  if (outcome === 'no_access') return <Navigate to='/noaccess' replace />;
  if (outcome !== 'ready') return <>{fallback}</>;
  return (
    <>
      <RolePageRecorder />
      {children}
    </>
  );
};

export default RoleGate;
